import type { NgZone } from '@angular/core';
import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type * as mapboxgl from 'mapbox-gl';
import type { Feature, Polygon as GJPolygon } from 'geojson';
import { allPolygons, selectedIds } from './map-draw-edits';
import { isCircleFeature } from './map-geometry.utils';
import type { MapHintMode } from './map-hint.controller';

export interface MapDrawInteractionState {
  isDrawing: boolean;
  isEditing: boolean;
}

export interface MapDrawInteractionContext {
  map: () => mapboxgl.Map;
  draw: () => MapboxDraw;
  state: MapDrawInteractionState;
  zone: NgZone;
  isStyleReady: () => boolean;
  setHint: (mode: MapHintMode) => void;
  refreshCtrl: () => void;
  markForCheck: () => void;
  scrollToPolygon: (id: string) => void;
  undo: () => void;
  redo: () => void;
}

const CIRCLE_HIDDEN_LAYERS = {
  vertex: ['gl-draw-polygon-and-line-vertex-halo-active', 'gl-draw-polygon-and-line-vertex-active'],
  midpoint: ['gl-draw-polygon-midpoint-halo', 'gl-draw-polygon-midpoint'],
};
const ACTIVE_STROKE_LAYER = 'gl-draw-polygon-stroke-active';
const POLYGON_FILL_LAYERS = ['gl-draw-polygon-fill-active', 'gl-draw-polygon-fill-inactive'];

export class MapDrawInteractionController {
  private animationFrame: number | null = null;
  private attachedCanvas: HTMLCanvasElement | null = null;

  constructor(private readonly context: MapDrawInteractionContext) {}

  attach(): void {
    const map = this.context.map();
    map.on('draw.modechange', this.onModeChange);
    map.on('draw.selectionchange', this.onSelectionChange);
    map.on('mousedown', this.onMouseDownForceDirectSelect);
    this.attachedCanvas = map.getCanvas() as HTMLCanvasElement;
    this.attachedCanvas.setAttribute('tabindex', '0');
    this.attachedCanvas.addEventListener('keydown', this.onCanvasKeydown);
  }

  detach(): void {
    try {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    } catch {}
    const map = this.context.map();
    if (!map) return;
    this.attachedCanvas?.removeEventListener('keydown', this.onCanvasKeydown);
    map.off('draw.modechange', this.onModeChange);
    map.off('draw.selectionchange', this.onSelectionChange);
    map.off('mousedown', this.onMouseDownForceDirectSelect);
  }

  hideCircleVertices(): void {
    if (!this.context.isStyleReady()) return;
    const circleIds = allPolygons(this.context.draw())
      .filter((f) => isCircleFeature(f))
      .map((f: any) => String(f.id))
      .filter(Boolean);
    const ids: any = ['literal', circleIds];
    const belongsToCircle: any = ['any', ['in', ['get', 'parent'], ids], ['in', ['get', 'featureId'], ids]];
    this.filterLayers(CIRCLE_HIDDEN_LAYERS.vertex, 'vertex', belongsToCircle);
    this.filterLayers(CIRCLE_HIDDEN_LAYERS.midpoint, 'midpoint', belongsToCircle);
  }

  startStrokeAnimationWhenReady(): void {
    const map = this.context.map();
    const tryStart = () => {
      if (!this.strokeLayerReady()) {
        map.once('idle', tryStart);
        return;
      }
      this.context.zone.runOutsideAngular(() => this.animateStroke());
    };
    map.once('idle', tryStart);
  }

  private filterLayers(layerIds: string[], meta: 'vertex' | 'midpoint', belongsToCircle: any): void {
    const filter: any = ['all', ['==', '$type', 'Point'], ['==', 'meta', meta], ['!', belongsToCircle]];
    const map = this.context.map();
    for (const layerId of layerIds) {
      try {
        if (map.getLayer(layerId)) map.setFilter(layerId, filter);
      } catch (e) {
        console.error(e);
      }
    }
  }

  private strokeLayerReady(): boolean {
    return this.context.isStyleReady() && Boolean(this.context.map().getLayer(ACTIVE_STROKE_LAYER));
  }

  private animateStroke(): void {
    let phase = 0;
    const step = () => {
      if (!this.strokeLayerReady()) return;
      phase = (phase + 0.05) % 6;
      try {
        const on = Math.floor(phase) % 2 === 0;
        this.context.map().setPaintProperty(ACTIVE_STROKE_LAYER, 'line-dasharray', on ? [2, 2] : [1, 3]);
      } catch {}
      this.animationFrame = requestAnimationFrame(step);
    };
    this.animationFrame = requestAnimationFrame(step);
  }

  private enterDirectSelect(id: string): void {
    (this.context.draw() as any).changeMode('direct_select', { featureId: id });
    this.context.state.isEditing = true;
    this.context.setHint('edit');
  }

  private enterSimpleSelect(id: string): void {
    (this.context.draw() as any).changeMode('simple_select', { featureIds: [id] });
    this.context.state.isEditing = false;
    this.context.setHint('idle');
  }

  private leaveEditing(): void {
    try {
      (this.context.draw() as any).changeMode('simple_select');
    } catch {}
    this.context.state.isEditing = false;
    this.context.state.isDrawing = false;
    this.context.map().getCanvas().style.cursor = '';
    this.context.setHint('idle');
    this.context.refreshCtrl();
    this.context.markForCheck();
  }

  private onModeChange = (e: any) => {
    this.context.zone.run(() => {
      const mode = e?.mode as string | undefined;
      const enteringDraw = mode === 'draw_polygon';
      this.context.state.isDrawing = enteringDraw;
      this.context.state.isEditing = mode === 'direct_select';
      this.updateCursor(enteringDraw);
      this.context.setHint(enteringDraw ? 'draw' : this.context.state.isEditing ? 'edit' : 'idle');
      this.context.refreshCtrl();
      this.context.markForCheck();
    });
  };

  private updateCursor(drawing: boolean): void {
    try {
      const map = this.context.map();
      map.getCanvas().style.cursor = drawing ? 'crosshair' : '';
      if (drawing) map.doubleClickZoom.disable();
      else map.doubleClickZoom.enable();
    } catch {}
  }

  private onSelectionChange = () => {
    try {
      const draw = this.context.draw();
      const [id] = selectedIds(draw);
      if (id === undefined) {
        this.context.refreshCtrl();
        return;
      }
      const feature = draw.get(id) as Feature | undefined;
      if (feature?.geometry.type === 'Point') {
        this.context.state.isEditing = false;
        this.context.setHint('idle');
      } else if (feature && isCircleFeature(feature)) {
        this.enterSimpleSelect(id);
      } else if ((draw as any).getMode?.() === 'simple_select') {
        this.enterDirectSelect(id);
      }
      this.context.refreshCtrl();
      this.context.scrollToPolygon(id);
      this.context.markForCheck();
    } catch {}
  };

  private onCanvasKeydown = (ev: KeyboardEvent) => {
    const key = ev.key.toLowerCase();
    if ((ev.ctrlKey || ev.metaKey) && !ev.altKey && ['z', 'y'].includes(key)) {
      ev.preventDefault();
      if (ev.shiftKey || key === 'y') this.context.redo();
      else this.context.undo();
      return;
    }
    const { isDrawing, isEditing } = this.context.state;
    if (ev.key === 'Escape' && (isDrawing || isEditing)) {
      this.leaveEditing();
      return;
    }
    if ((ev.key === 'Backspace' || ev.key === 'Delete') && isEditing) {
      ev.preventDefault();
      try {
        (this.context.draw() as any).trash();
      } catch {}
    }
  };

  private onMouseDownForceDirectSelect = (e: mapboxgl.MapMouseEvent) => {
    try {
      const map = this.context.map();
      const draw = this.context.draw();
      if (!map || !draw) return;
      const layers = POLYGON_FILL_LAYERS.filter((id) => Boolean(map.getLayer(id)));
      if (!layers.length) return;
      const hit = map.queryRenderedFeatures(e.point, { layers }).find((h: any) => h.layer?.id?.startsWith('gl-draw-'));
      if (!hit) return;
      const targetId = (hit as any)['id'] ?? hit.properties?.['id'];
      if (targetId == null) return;
      const feature = draw.get(String(targetId)) as Feature<GJPolygon> | undefined;
      if ((feature?.properties as any)?._circle) return;
      if ((draw as any).getMode?.() === 'simple_select') this.enterDirectSelect(String(targetId));
    } catch {}
  };
}
